import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { notificationActions } from "../../data/notification";
import useT from "../../i18n/useT";

const money = (n) => "$" + (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CustomsCalculator = () => {
    const t = useT();
    const dispatch = useDispatch();
    const role = useSelector((state) => state.user.role);
    const isAdmin = role === "admin";
    const isStaff = role === "admin" || role === "moderator";

    const [form, setForm] = useState({ customs_value: "", engine_cc: "", age_years: "", fuel: "petrol" });
    const [result, setResult] = useState(null);
    const [rates, setRates] = useState(null);

    const notify = (text, type) => {
        dispatch(notificationActions.changeNotification({ show: true, text, type }));
        setTimeout(() => dispatch(notificationActions.changeNotification({ show: false, text: "", type: "" })), 4000);
    };

    useEffect(() => {
        if (!isStaff) return;
        axios.get("/api/customs-rates").then((res) => setRates(res.data.data)).catch(() => {});
    }, [isStaff]);

    const calc = (e) => {
        e.preventDefault();
        axios.post("/api/public/customs-calc", form)
            .then((res) => setResult(res.data.data))
            .catch(() => {});
    };

    const saveRates = () => {
        axios.put("/api/customs-rates", rates)
            .then((res) => notify(res.data.message.en, "success-notification"))
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"));
    };

    const rateField = (key, label) => (
        <div style={{ flex: "1 1 120px" }}>
            <label style={{ fontSize: "12px", opacity: 0.7, display: "block" }}>{label}</label>
            <input type="number" step="0.0001" className="form-control" value={rates[key]} onChange={(e) => setRates((r) => ({ ...r, [key]: e.target.value }))} />
        </div>
    );

    const row = (label, value, strong) => (
        <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)", fontWeight: strong ? 700 : 400 }}>
            <span>{label}</span><span>{money(value)}</span>
        </div>
    );

    return (
        <div className="page-content-main-container">
            <h2 className="page-title">{t("customs.title")}</h2>

            <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                <form onSubmit={calc} style={{ flex: "1 1 300px", maxWidth: "380px", background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "18px" }}>
                    <div style={{ marginBottom: "10px" }}>
                        <label style={{ fontSize: "12px", opacity: 0.7, display: "block" }}>{t("customs.value")}</label>
                        <input type="number" step="0.01" min="0" className="form-control" value={form.customs_value} onChange={(e) => setForm((p) => ({ ...p, customs_value: e.target.value }))} required />
                    </div>
                    <div style={{ marginBottom: "10px" }}>
                        <label style={{ fontSize: "12px", opacity: 0.7, display: "block" }}>{t("customs.engine")}</label>
                        <input type="number" min="0" className="form-control" value={form.engine_cc} onChange={(e) => setForm((p) => ({ ...p, engine_cc: e.target.value }))} />
                    </div>
                    <div style={{ marginBottom: "10px" }}>
                        <label style={{ fontSize: "12px", opacity: 0.7, display: "block" }}>{t("customs.age")}</label>
                        <input type="number" min="0" className="form-control" value={form.age_years} onChange={(e) => setForm((p) => ({ ...p, age_years: e.target.value }))} />
                    </div>
                    <div style={{ marginBottom: "16px" }}>
                        <label style={{ fontSize: "12px", opacity: 0.7, display: "block" }}>{t("customs.fuel")}</label>
                        <select className="form-control" value={form.fuel} onChange={(e) => setForm((p) => ({ ...p, fuel: e.target.value }))}>
                            <option value="petrol">{t("customs.fuel_petrol")}</option>
                            <option value="electric">{t("customs.fuel_electric")}</option>
                        </select>
                    </div>
                    <button type="submit" className="btn btn-primary">{t("customs.calc")}</button>
                </form>

                {result && (
                    <div style={{ flex: "1 1 300px", maxWidth: "380px", background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "18px" }}>
                        {row(t("customs.importDuty"), result.importDuty)}
                        {row(t("customs.excise"), result.excise)}
                        {row(t("customs.vat"), result.vat)}
                        {row(t("customs.clearanceFee"), result.clearanceFee)}
                        {row(t("customs.total"), result.total, true)}
                        <div style={{ fontSize: "11px", opacity: 0.55, marginTop: "12px" }}>{t("customs.disclaimer")}</div>
                    </div>
                )}
            </div>

            {isStaff && rates && (
                <div style={{ marginTop: "26px", maxWidth: "760px" }}>
                    <h3 style={{ fontSize: "15px", marginBottom: "12px" }}>{t("customs.rates")}</h3>
                    <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "18px", display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end" }}>
                        {rateField("vat_percent", t("customs.vatPercent"))}
                        {rateField("import_duty_percent", t("customs.dutyPercent"))}
                        {rateField("excise_per_cc", t("customs.excisePerCc"))}
                        {rateField("clearance_fee", t("customs.clearance"))}
                        {rateField("age_new_max", t("customs.ageNewMax"))}
                        {rateField("age_old_min", t("customs.ageOldMin"))}
                        {rateField("coef_new", t("customs.coefNew"))}
                        {rateField("coef_mid", t("customs.coefMid"))}
                        {rateField("coef_old", t("customs.coefOld"))}
                        {isAdmin && <button className="btn btn-primary" onClick={saveRates}>{t("customs.saveRates")}</button>}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomsCalculator;
